/**
 * Derived-selector composers for form validation errors.
 *
 * This module stitches individual rules from `rules.ts` into higher-level aggregators that
 * match the shape each slice needs. Each aggregator is a plain, pure function over inputs
 * the slice obtains via its entity-adapter selectors; memoisation is added at the slice
 * level via `createSelector` when the aggregator is wired in.
 */
import {
    AeTitleShape,
    checkAeTitleUniqueness,
    checkDicomAttributesInRule,
    checkDicomRuleHasAtLeastOneAttribute,
    checkDoseRingDistances,
    checkRoiNamesInOutput,
    DicomAttributeShape,
    DicomRuleShape,
    RoiShape,
} from "./rules";
import { FormValidationError } from "../store-errors";


/**
 * Aggregates trigger-level validation errors for a whole slice: DICOM rule / DICOM attribute /
 * AE Title uniqueness. This is the subset of rules that every customization slice shares.
 *
 * Inputs:
 * - `dicomRules`: all DICOM rules in the slice.
 * - `dicomAttributes`: all DICOM attribute rules in the slice. The composer filters per rule.
 * - `aeTitleRules`: all AE title rules in the slice.
 *
 * AE title uniqueness is checked slice-global (all rules in the slice, across customization bases).
 */
export const composeTriggerValidationErrors = (
    dicomRules: DicomRuleShape[],
    dicomAttributes: DicomAttributeShape[],
    aeTitleRules: AeTitleShape[],
): FormValidationError[] => {
    // index attributes by id once so the per-rule check is O(K) in this rule's attributes
    // rather than O(M) in total slice attributes — keeps `selectFormValidationErrors`
    // O(N+M) overall instead of O(N*M)
    const attributesById = new Map<string, DicomAttributeShape>();
    for (const attr of dicomAttributes) {
        attributesById.set(attr.id, attr);
    }

    const errors: FormValidationError[] = [];
    for (const rule of dicomRules) {
        errors.push(...checkDicomRuleHasAtLeastOneAttribute(rule));
        errors.push(...checkDicomAttributesInRule(rule, attributesById));
    }
    errors.push(...checkAeTitleUniqueness(aeTitleRules));
    return errors;
};

/**
 * Aggregates Contour+ output-level validation errors for a single output's ROIs.
 *
 * Callers pass the full ROI list for one customization output. Repeat per output, flatten.
 */
export const composeContourOutputValidationErrors = (rois: RoiShape[]): FormValidationError[] =>
    checkRoiNamesInOutput(rois);

/**
 * Aggregates Dose+ output-level validation errors for a single output.
 *
 * Callers pass the output's `id`, ring-creation enablement flag, and the ring distances.
 */
export const composeDoseOutputValidationErrors = (
    outputId: string,
    isRoiCreationEnabled: boolean,
    ringDistances: number[],
): FormValidationError[] =>
    checkDoseRingDistances(outputId, isRoiCreationEnabled, ringDistances);
